import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, AutoComplete, DatePicker, Upload, Divider, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, InboxOutlined, FileTextOutlined,
    PlusOutlined, AppstoreAddOutlined, ScanOutlined, DeleteOutlined,
} from "@ant-design/icons"
import { GoodsReceiptService } from '../../../Services/Manage/GoodsReceiptService'
import { PurchaseOrderService } from '../../../Services/Manage/PurchaseOrderService'
import { ItemService } from '../../../Services/Manage/ItemService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
  .gr-equal-height .ant-upload-drag { height: 132px; }
  .gr-equal-height textarea.ant-input { height: 132px !important; resize: none; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const gstOptions = [0, 5, 12, 18, 28].map((v) => ({ value: v, label: `${v}% GST` }));

const AddGoodsReceipt = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [grNumber, setGrNumber] = useState("GR-2024-000001");
    const [allPOs, setAllPOs] = useState([]);
    const [usedPoIds, setUsedPoIds] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [receiptTypeOptions, setReceiptTypeOptions] = useState([]);
    const [transporterOptions, setTransporterOptions] = useState([]);
    const [discountValue, setDiscountValue] = useState(0);
    const [poDiscountReference, setPoDiscountReference] = useState(0);

    const [rows, setRows] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");

    const [scanModalOpen, setScanModalOpen] = useState(false);
    const [scanValue, setScanValue] = useState("");

    const [editGR, setEditGR] = useState(null);
    const [saving, setSaving] = useState(false);
    const [documentFiles, setDocumentFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const from = location.state?.from || '/inventory/goods-receipts';
    const isReadOnly = !enableEdit && !!editGR;
    const firstOf = (v) => (Array.isArray(v) ? v[0] : v);

    const getConfigOptions = async () => {
        try {
            const fields = ["warehouse", "receipt_type", "transporter"];
            const results = await Promise.all(fields.map((f) => GoodsReceiptService.getDistinctValues(f)));
            const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));
            setWarehouseOptions(toOptions(results[0].data));
            setReceiptTypeOptions(toOptions(results[1].data));
            setTransporterOptions(toOptions(results[2].data));
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getAllPOs();
        getConfigOptions();
        getAllItems();
        getUsedPoIds();
        if (id) {
            getGRById(id);
        } else {
            getLastGR();
        }
    }, [id]);

    const uploadOneFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${ENDPOINT.GoodsReceipt}/upload-file`, { method: "POST", body: formData });
        const result = await res.json();
        return result?.status && result?.success ? result.file : null;
    };
    const getAllItems = async () => {
        try {
            const request = await ItemService.getAllItem();
            const active = (request.data || []).filter((i) => i.is_active);
            setAllItems(active);
        } catch (error) { console.error(error); }
    };

    const getAllPOs = async () => {
        try {
            const request = await PurchaseOrderService.getAllPO();
            const data = request.data || [];
            setAllPOs(data);
        } catch (error) { console.error(error); }
    };

    const getUsedPoIds = async () => {
        try {
            const request = await GoodsReceiptService.getAllGR();
            const ids = (request.data || []).map((gr) => gr.po_id).filter((v) => v !== null && v !== undefined);
            setUsedPoIds(ids);
        } catch (error) { console.error(error); }
    };

    const getLastGR = async () => {
        try {
            const request = await GoodsReceiptService.getLastGR();
            let nextNo = "000001";
            if (request?.status && request?.data?.gr_number) {
                const seqPart = request.data.gr_number.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) nextNo = String(parsed + 1).padStart(6, "0");
            }
            setGrNumber(`GR-${dayjs().format("YYYY")}-${nextNo}`);
        } catch (error) { console.error(error); }
    };

    const getGRById = async (id) => {
        try {
            const request = await GoodsReceiptService.getGRById(id);
            const initialValues = request.data;
            setEditGR(initialValues);
            setGrNumber(initialValues.gr_number);
            setRows((initialValues.items || []).map((it, idx) => ({ ...it, key: idx })));

            let parsedAttachments = [];
            try {
                parsedAttachments = initialValues.attachments
                    ? (typeof initialValues.attachments === "string" ? JSON.parse(initialValues.attachments) : initialValues.attachments)
                    : [];
            } catch { parsedAttachments = []; }

            setExistingAttachments(
                (parsedAttachments || []).map((f, idx) => ({
                    uid: f.uid || `existing-${idx}`,
                    name: f.name || f.filename || `file-${idx}`,
                    ...f,
                    status: "done",
                    url: `${ENDPOINT.base}/${f.path || f.url}`,
                }))
            );

            setDiscountValue(Number(initialValues.discount_value || 0));

            form.setFieldsValue({
                ...initialValues,
                warehouse: initialValues.warehouse ? [initialValues.warehouse] : [],
                receipt_type: initialValues.receipt_type ? [initialValues.receipt_type] : [],
                transporter: initialValues.transporter ? [initialValues.transporter] : [],
                receipt_date: initialValues.receipt_date ? dayjs(initialValues.receipt_date) : null,
                delivery_date: initialValues.delivery_date ? dayjs(initialValues.delivery_date) : null,
            });
        } catch (error) { console.error(error); }
    };

    const onPOChange = async (poId) => {
        const po = allPOs.find((x) => Number(x.id) === Number(poId));
        if (!po) return;
        form.setFieldsValue({
            supplier_id: po.supplier_id,
            supplier_name: po.supplier_name,
        });
        try {
            const request = await PurchaseOrderService.getPOById(poId);
            const poDetail = request.data;
            console.log("PO detail fetched:", poDetail);
            console.log("PO discount field:", poDetail.discount, "PO sub_total field:", poDetail.sub_total);

            const items = (poDetail.items || []).map((it, idx) => ({
                key: idx,
                po_item_id: it.id || null,
                item_id: it.item_id,
                item_name: it.item_name,
                description: it.description || "",
                hsn_sku: it.hsn_sku || "",
                unit: it.unit || "",
                ordered_qty: Number(it.qty || 0),
                received_qty: Number(it.qty || 0),
                pending_qty: 0,
                damage_qty: 0,
                unit_cost: Number(it.rate || 0),
                discount_percent: Number(it.discount_percent || 0),
                tax_percent: Number(it.tax_percent || 0),
                batch_no: "",
                expiry_date: null,
            }));
            items.forEach((it) => { it.amount = calcRowAmount(it); });
            setRows(items);
            console.log("Rows set from PO items:", items);

            // Each item already carries its own discount_percent copied from
            // the PO line, which reproduces the PO's item-level discount.
            // Also capture the PO's raw discount amount for display/reference
            // so "Total Discount from PO" is visible even before rows compute.
            const poTotalDiscountAmount = Number(poDetail.discount || 0);
            setPoDiscountReference(poTotalDiscountAmount);
            console.log("PO Total Discount amount captured:", poTotalDiscountAmount);

            setDiscountValue(0);
        } catch (error) {
            console.error("Error in onPOChange:", error);
        }
    };

    const calcRowAmount = (row) => {
        const qty = Number(row.received_qty || 0);
        const rate = Number(row.unit_cost || 0);
        const discountPercent = Number(row.discount_percent || 0);
        const taxPercent = Number(row.tax_percent || 0);
        const base = qty * rate;
        const discountAmt = (base * discountPercent) / 100;
        const taxable = base - discountAmt;
        const taxAmt = (taxable * taxPercent) / 100;
        console.log("calcRowAmount:", { item: row.item_name, qty, rate, discountPercent, discountAmt, taxPercent, taxAmt });
        return Math.round(taxable + taxAmt);
    };

    const handleRowChange = (key, field, value) => {
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            const updated = { ...row, [field]: value };
            updated.pending_qty = Math.max(Number(updated.ordered_qty || 0) - Number(updated.received_qty || 0), 0);
            updated.amount = calcRowAmount(updated);
            return updated;
        }));
    };

    const handleAddRow = () => {
        setRows((prev) => [...prev, {
            key: Date.now(), po_item_id: null, item_id: null, item_name: "", description: "",
            hsn_sku: "", unit: "", ordered_qty: 0, received_qty: 0, pending_qty: 0, damage_qty: 0,
            unit_cost: 0, discount_percent: 0, tax_percent: 0, batch_no: "", expiry_date: null, amount: 0,
        }]);
    };

    const handleDeleteRow = (key) => {
        setRows((prev) => prev.filter((r) => r.key !== key));
    };

    const handleSelectItemForRow = (key, itemId) => {
        const selected = allItems.find((x) => Number(x.id) === Number(itemId));
        if (!selected) return;
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            const updated = {
                ...row,
                item_id: selected.id,
                item_name: selected.item_name,
                description: selected.description || "",
                hsn_sku: selected.hsn_sac_code || selected.sku_barcode || "",
                unit: selected.unit || "",
                unit_cost: Number(selected.cost_price || 0),
                tax_percent: Number(selected.tax_rate || 0),
                ordered_qty: row.ordered_qty || 0,
                received_qty: row.received_qty || 0,
            };
            updated.amount = calcRowAmount(updated);
            return updated;
        }));
        setItemSearch("");
    };

    const handleClearItemRow = (key) => {
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            return {
                ...row,
                item_id: null,
                item_name: "",
                description: "",
                hsn_sku: "",
                unit: "",
                unit_cost: 0,
                tax_percent: 0,
                amount: 0,
            };
        }));
        setItemSearch("");
    };


    const handleBarcodeScanned = (rawValue) => {
        const code = (rawValue || "").trim();
        if (!code) {
            messageApi.warning("Please enter a barcode number");
            return;
        }

        const matched = allItems.find((i) => String(i.barcode || i.sku_barcode).trim() === code);

        if (!matched) {
            messageApi.warning(`No item found for barcode: ${code}`);
            return;
        }

        const emptyRow = rows.find((r) => !r.item_id && !r.po_item_id);
        if (emptyRow) {
            handleSelectItemForRow(emptyRow.key, matched.id);
        } else {
            const newKey = Date.now();
            const newRow = {
                key: newKey, po_item_id: null, item_id: matched.id, item_name: matched.item_name,
                description: matched.description || "", hsn_sku: matched.hsn_sac_code || matched.sku_barcode || "",
                unit: matched.unit || "", ordered_qty: 0, received_qty: 1, pending_qty: 0, damage_qty: 0,
                unit_cost: Number(matched.cost_price || 0), discount_percent: 0,
                tax_percent: Number(matched.tax_rate || 0), batch_no: "", expiry_date: null, amount: 0,
            };
            newRow.amount = calcRowAmount(newRow);
            setRows((prev) => [...prev, newRow]);
        }

        messageApi.success(`Added: ${matched.item_name}`);
        setScanValue("");
    };

    const itemAutoOptions = allItems
        .filter((i) => !itemSearch || i.item_name.toLowerCase().includes(itemSearch.toLowerCase()))
        .map((i) => ({ value: String(i.id), label: `${i.item_name}${i.sku_barcode ? ` (${i.sku_barcode})` : ""}` }));

    const currentPoId = editGR?.po_id;
    const poOptions = editGR
        ? allPOs.filter((p) => p.id === currentPoId).map((p) => ({ value: p.id, label: p.po_number }))
        : allPOs.filter((p) => !usedPoIds.includes(p.id)).map((p) => ({ value: p.id, label: p.po_number }));

    const subTotal = rows.reduce((sum, r) => sum + Number(r.received_qty || 0) * Number(r.unit_cost || 0), 0);
    const itemLevelDiscount = rows.reduce((sum, r) => sum + (Number(r.received_qty || 0) * Number(r.unit_cost || 0) * Number(r.discount_percent || 0)) / 100, 0);
    const headerDiscountAmount = (subTotal * Number(discountValue || 0)) / 100;
    const totalDiscount = itemLevelDiscount + headerDiscountAmount;
    const taxableAmount = Math.max(subTotal - totalDiscount, 0);
    const totalGst = rows.reduce((sum, r) => {
        const base = Number(r.received_qty || 0) * Number(r.unit_cost || 0);
        const discountAmt = (base * Number(r.discount_percent || 0)) / 100;
        const taxable = base - discountAmt;
        return sum + (taxable * Number(r.tax_percent || 0)) / 100;
    }, 0);
    const totalAmount = Math.round(taxableAmount + totalGst);
    const totalOrderedQty = rows.reduce((sum, r) => sum + Number(r.ordered_qty || 0), 0);
    const totalReceivedQty = rows.reduce((sum, r) => sum + Number(r.received_qty || 0), 0);
    const totalDamageQty = rows.reduce((sum, r) => sum + Number(r.damage_qty || 0), 0);

    console.log("Totals:", { subTotal, itemLevelDiscount, headerDiscountAmount, totalDiscount, taxableAmount, totalGst, totalAmount, poDiscountReference });

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        if (!rows.length) {
            messageApi.warning("Please select a purchase order with items");
            return;
        }
        setSaving(true);
        try {
            
            let uploadedAttachments = [...existingAttachments];
            for (const f of documentFiles) {
                const uploaded = await uploadOneFile(f);
                if (uploaded) uploadedAttachments.push(uploaded);
            }

            const selectedPO = allPOs.find((x) => Number(x.id) === Number(values.po_id));

            const anyDamage = rows.some((r) => Number(r.damage_qty || 0) > 0);
            const fullyReceived = rows.every((r) => Number(r.received_qty || 0) >= Number(r.ordered_qty || 0));
            const anyReceived = rows.some((r) => Number(r.received_qty || 0) > 0);
            const status = anyDamage && !fullyReceived
                ? "Returned"
                : fullyReceived
                    ? "Received"
                    : anyReceived
                        ? "Partial Received"
                        : "Pending";

            const payload = {
                attachments: JSON.stringify(uploadedAttachments),
                ...values,
                warehouse: firstOf(values.warehouse),
                receipt_type: firstOf(values.receipt_type),
                transporter: firstOf(values.transporter),
                discount_value: discountValue,

                gr_number: grNumber,
                po_number: selectedPO?.po_number || editGR?.po_number || "",
                receipt_date: values.receipt_date ? dayjs(values.receipt_date).format("YYYY-MM-DD") : null,
                delivery_date: values.delivery_date ? dayjs(values.delivery_date).format("YYYY-MM-DD") : null,
                sub_total: subTotal,
                discount: totalDiscount,
                taxable_amount: taxableAmount,
                total_gst: totalGst,
                total_amount: totalAmount,
                status: status,
                added_by: editGR ? editGR.added_by : currentUserId,
                items: rows.map(({ key, ...rest }) => rest),
            };

            let request;
            if (editGR) {
                request = await GoodsReceiptService.updateGR(payload, id);
            } else {
                request = await GoodsReceiptService.createGR(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const request = await GoodsReceiptService.deleteGR(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request.message);
            }
        } catch (error) { console.error(error); }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined style={{ cursor: "pointer", fontSize: "20px" }} onClick={() => navigate(from)} />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {editGR ? `${action} Goods Receipt` : 'New Goods Receipt'}{' '}
                                    {editGR && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            {editGR.gr_number}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editGR ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/goods-receipts/edit-goods-receipt", { state: { id, action: 'Edit', enableEdit: true, from } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editGR ? "Update Goods Receipt" : "Save Goods Receipt"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form layout="vertical" form={form} onFinish={onFinish} size="middle" disabled={isReadOnly}>
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Receipt Details" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="GR Number">
                                            <Input size="large" readOnly value={grNumber} placeholder="Auto generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="po_id" label="PO Number" rules={[{ required: true, message: 'Please select purchase order' }]}>
                                            <Select
                                                options={poOptions} size="large" showSearch allowClear placeholder="Select Purchase Order"
                                                onChange={onPOChange}
                                                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="supplier_name" label="Supplier">
                                            <Input size="large" readOnly placeholder="Auto-filled from PO" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="receipt_date" label="Receipt Date">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="warehouse" label="Warehouse" rules={[{ required: true, message: 'Please select warehouse' }]}>
                                            <Select options={warehouseOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type warehouse" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="receipt_type" label="Receipt Type" rules={[{ required: true, message: 'Please select receipt type' }]}>
                                            <Select options={receiptTypeOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type receipt type" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reference_no" label="Reference No.">
                                            <Input size="large" placeholder="Enter reference number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="challan_no" label="Challan / Delivery No.">
                                            <Input size="large" placeholder="Enter challan number" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="transporter" label="Transporter (Optional)">
                                            <Select options={transporterOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type transporter" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="vehicle_no" label="Vehicle No. (Optional)">
                                            <Input size="large" placeholder="Enter vehicle number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="delivery_date" label="Delivery Date">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="remarks" label="Remarks">
                                            <TextArea rows={2} maxLength={250} showCount placeholder="Enter remarks (optional)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                            <Card
                                title={<CardTitle icon={<InfoCircleOutlined />} no={2} text="Items Received" />}
                                extra={
                                    <Space>
                                        <Button icon={<PlusOutlined />} type="primary" onClick={handleAddRow}
                                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                                            Add Item
                                        </Button>
                                        {/* <Button icon={<AppstoreAddOutlined />}>Add Multiple Items</Button> */}
                                        <Button icon={<ScanOutlined />} onClick={() => setScanModalOpen(true)}>Scan Barcode</Button>                                    </Space>
                                }
                            >
                                {rows.length > 0 && (
                                    <Row gutter={8} style={{ padding: "0 8px", marginBottom: 6 }}>
                                        <Col xs={0} md={1}><Text type="secondary" strong style={{ fontSize: 12 }}>#</Text></Col>
                                        <Col xs={24} md={4}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>Item / Product</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Description</Text>
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>HSN/SKU</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Unit</Text>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>Ordered Qty</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Pending Qty</Text>
                                        </Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Received Qty</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Unit Cost (₹)</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Discount (%)</Text></Col>
                                        <Col xs={6} md={3}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>Batch / Lot No.</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Expiry Date</Text>
                                        </Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Damage Qty</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Amount (₹)</Text></Col>
                                        <Col xs={6} md={1}></Col>
                                    </Row>
                                )}

                                {rows.map((row, idx) => (
                                    <Row key={row.key} gutter={8} align="middle" style={{ marginBottom: 8, border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
                                        <Col xs={0} md={1}><Text type="secondary">{idx + 1}</Text></Col>
                                        <Col xs={24} md={4}>
                                            {row.po_item_id ? (
                                                <>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{row.item_name}</div>
                                                    <div style={{ fontSize: 12, color: '#888' }}>{row.description}</div>
                                                </>
                                            ) : (
                                                // <AutoComplete
                                                //     style={{ width: "100%" }}
                                                //     options={itemAutoOptions}
                                                //     value={row.item_name}
                                                //     placeholder="Type item / product"
                                                //     filterOption={false}
                                                //     allowClear
                                                //     onSearch={(val) => setItemSearch(val)}
                                                //     onSelect={(val) => handleSelectItemForRow(row.key, val)}
                                                //     onChange={(val) => handleRowChange(row.key, "item_name", val)}
                                                // />

                                                <AutoComplete
                                                    style={{ width: "100%" }}
                                                    options={itemAutoOptions}
                                                    value={row.item_name}
                                                    placeholder="Search item / product"
                                                    filterOption={false}
                                                    allowClear
                                                    notFoundContent="No matched items / products"
                                                    onSearch={(val) => {
                                                        setItemSearch(val);
                                                        handleRowChange(row.key, "item_name", val);
                                                    }}
                                                    onSelect={(val) => handleSelectItemForRow(row.key, val)}
                                                    onClear={() => handleClearItemRow(row.key)}
                                                    onBlur={() => {
                                                        if (!row.item_id) {
                                                            handleClearItemRow(row.key);
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <div style={{ fontSize: 13 }}>{row.hsn_sku}</div>
                                            <div style={{ fontSize: 12, color: '#888' }}>{row.unit}</div>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} style={{ width: '100%' }} value={row.ordered_qty}
                                                readOnly={Number(row.ordered_qty || 0) > 0}
                                                onChange={(v) => handleRowChange(row.key, "ordered_qty", v)} />
                                            <div style={{ fontSize: 11, color: '#faad14', marginTop: 2 }}>Pending: {Number(row.pending_qty || 0).toFixed(2)}</div>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} style={{ width: '100%' }} value={row.received_qty}
                                                onChange={(v) => handleRowChange(row.key, "received_qty", v)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} style={{ width: '100%' }} value={row.unit_cost}
                                                onChange={(v) => handleRowChange(row.key, "unit_cost", v)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} max={100} style={{ width: '100%' }} value={row.discount_percent}
                                                onChange={(v) => handleRowChange(row.key, "discount_percent", v)} />
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Input size="middle" placeholder="Batch No." value={row.batch_no}
                                                onChange={(e) => handleRowChange(row.key, "batch_no", e.target.value)} style={{ marginBottom: 4 }} />
                                            <DatePicker size="middle" style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="Expiry"
                                                value={row.expiry_date ? dayjs(row.expiry_date) : null}
                                                onChange={(d) => handleRowChange(row.key, "expiry_date", d ? d.format("YYYY-MM-DD") : null)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <InputNumber min={0} style={{ width: '100%' }} value={row.damage_qty}
                                                onChange={(v) => handleRowChange(row.key, "damage_qty", v)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Text strong>₹ {Number(row.amount || 0).toLocaleString('en-IN')}</Text>
                                        </Col>
                                        <Col xs={6} md={1}>
                                            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDeleteRow(row.key)} />
                                        </Col>
                                    </Row>
                                ))}
                                {rows.length === 0 && (
                                    <Text type="secondary">Select a Purchase Order above to load its items</Text>
                                )}
                            </Card>

                            <Card title={<CardTitle icon={<FileTextOutlined />} no={3} text="Additional Information" />} className="gr-equal-height">
                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <Form.Item label="Attachments">
                                            <Upload.Dragger
                                                multiple
                                                beforeUpload={(file) => { setDocumentFiles((prev) => [...prev, file]); return false; }}
                                                onRemove={(file) => {
                                                    if (file.status === "done") {
                                                        setExistingAttachments((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    } else {
                                                        setDocumentFiles((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    }
                                                }}
                                                fileList={[...existingAttachments, ...documentFiles]}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click or drag files to upload</p>
                                                <p className="ant-upload-hint">PNG, JPG, PDF (Max. 5MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="quality_checked_by" label="Quality Checked By">
                                            <TextArea placeholder="Enter checker name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="notes" label="Notes">
                                            <TextArea maxLength={250} showCount placeholder="Enter notes (optional)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>

                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => navigate(from)}>Cancel</Button>
                                    {editGR && (
                                        <Button size="large" danger onClick={handleDelete}>Delete</Button>
                                    )}
                                    <Button
                                        type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editGR ? "Update Goods Receipt" : "Save Goods Receipt"}
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Receipt Summary</span>} size="small">
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Total Items", rows.length],
                                    ["Total Ordered Qty", totalOrderedQty.toFixed(2)],
                                    ["Total Received Qty", totalReceivedQty.toFixed(2)],
                                    ["Total Damage Qty", totalDamageQty.toFixed(2)],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                                <Divider style={{ margin: "6px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Sub Total</Text>
                                    <Text strong>₹ {subTotal.toLocaleString('en-IN')}</Text>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                                    <Text type="secondary">Discount</Text>
                                    <InputNumber
                                        min={0}
                                        size="small"
                                        style={{ width: 110 }}
                                        value={totalDiscount}
                                        onChange={(v) => setDiscountValue(v || 0)}
                                        disabled={isReadOnly}
                                        suffix="%"
                                    />
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Taxable Amount</Text>
                                    <Text strong>₹ {taxableAmount.toLocaleString('en-IN')}</Text>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Total GST</Text>
                                    <Text strong>₹ {totalGst.toLocaleString('en-IN')}</Text>
                                </div>
                                <Divider style={{ margin: "6px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
                                    <Text strong>Total Amount</Text>
                                    <Text strong style={{ color: "rgba(14, 93, 146, 0.95)" }}>₹ {totalAmount.toLocaleString('en-IN')}</Text>
                                </div>
                                {headerDiscountAmount > 0 && (
                                    <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#389e0d' }}>
                                        You will save ₹ {headerDiscountAmount.toLocaleString('en-IN')} by availing discount on this receipt.
                                    </div>
                                )}
                            </Space>
                        </Card>
                    </Col>
                </Row>


                <Modal
                    title="Enter Barcode"
                    open={scanModalOpen}
                    onCancel={() => { setScanModalOpen(false); setScanValue(""); }}
                    footer={null}
                    destroyOnClose
                >
                    <Space.Compact style={{ width: "100%" }}>
                        <Input
                            autoFocus
                            size="large"
                            placeholder="Enter barcode number..."
                            value={scanValue}
                            onChange={(e) => setScanValue(e.target.value)}
                            onPressEnter={() => handleBarcodeScanned(scanValue)}
                        />
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => handleBarcodeScanned(scanValue)}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            Enter
                        </Button>
                    </Space.Compact>
                </Modal>
            </Card>
        </>
    );
};

export default AddGoodsReceipt;